import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { insights } from "@/mocks/insights";
import { SITE_URL, SITE_NAME, COMPANY } from "@/config/site";

interface PageMeta {
  title: string;
  description: string;
  ogType?: "website" | "article";
  keywords: string[];
}

const defaultTitle = "Mitaja Corporation | Government & Enterprise IT Services";
const defaultDescription =
  "Mitaja Corporation delivers enterprise IT infrastructure, network engineering, database services, application support, and specialized technical professionals for government and commercial organizations.";
const defaultKeywords = [
  "government IT services",
  "IT professional services",
  "IT staff augmentation",
  "federal government IT contractor",
  "government technology services",
];

const coreKeywords = [
  "government IT services",
  "government technology services",
  "IT professional services",
  "IT staff augmentation",
  "federal government IT contractor",
];

const pageMeta: Record<string, PageMeta> = {
  "/": {
    title: "Mitaja Corporation | Government & Enterprise IT Services",
    description:
      "Mitaja delivers infrastructure, database, cloud, application, and technical staffing services for government and enterprise programs.",
    keywords: coreKeywords,
  },
  "/capabilities": {
    title: "IT Capabilities | Mitaja Corporation",
    description:
      "Explore Mitaja's infrastructure, database, cloud, application, Microsoft, web, mobile, AI, and technical staffing capabilities.",
    keywords: [
      "IT professional services",
      "software development services",
      "systems integration",
      "IT engineering services",
      "application development",
    ],
  },
  "/capabilities/infrastructure-network-engineering": {
    title: "Infrastructure & Network Engineering | Mitaja",
    description:
      "Enterprise network engineering, virtualization, Windows Server, VMware, modernization, migration, and infrastructure support.",
    keywords: ["IT engineering services", "systems integration", "IT infrastructure", "network engineering"],
  },
  "/capabilities/database-data-platform": {
    title: "Database & Data Platform Services | Mitaja",
    description:
      "Oracle and SQL Server engineering, administration, schema management, data integrity, reporting, and platform support.",
    keywords: ["database services", "database administration", "SQL Server", "Oracle", "data platform services"],
  },
  "/capabilities/application-platform-engineering": {
    title: "Application & Platform Engineering | Mitaja",
    description:
      "Enterprise application support, WebSphere, integration, platform engineering, maintenance, testing, and technical delivery.",
    keywords: ["application development", "application maintenance and support", "systems integration", "software development services"],
  },
  "/capabilities/microsoft-solutions": {
    title: "Microsoft Power Platform & SharePoint | Mitaja",
    description:
      "Power Apps, Power Automate, Power BI, SharePoint, workflow integration, implementation, training, and user enablement.",
    keywords: ["Microsoft Power Platform", "SharePoint", "workflow automation", "application development"],
  },
  "/capabilities/web-digital-solutions": {
    title: "Government Web & Digital Solutions | Mitaja",
    description:
      "Accessible websites, CMS modernization, web portals, content migration, integration, and public-sector digital solutions.",
    keywords: ["website development", "web application development", "CMS development", "government website services"],
  },
  "/capabilities/mobile-application-development": {
    title: "Mobile Application Development | Mitaja",
    description:
      "Secure cross-platform mobile applications, API integration, testing, and accessibility-aware mobile user experiences.",
    keywords: ["application development", "mobile application development", "API integration", "software development services"],
  },
  "/capabilities/ai-intelligent-engineering": {
    title: "AI & Intelligent Engineering Services | Mitaja",
    description:
      "AI-assisted engineering, workflow automation, data pipelines, business intelligence, and responsible machine-learning integration.",
    keywords: ["AI engineering", "workflow automation", "data pipelines", "business intelligence", "application development"],
  },
  "/capabilities/cloud-services": {
    title: "Cloud Services | Mitaja Corporation",
    description:
      "Microsoft Azure cloud engineering, IaaS, PaaS, hybrid cloud, migration, DevOps, and governance for enterprise and government programs.",
    keywords: ["cloud services", "Microsoft Azure", "cloud migration", "IT engineering services"],
  },
  "/experience": {
    title: "Government & Enterprise Experience | Mitaja",
    description:
      "Review Mitaja's documented technology-delivery experience across federal, state, local, international, and commercial programs.",
    keywords: ["government IT services", "IT professional services", "past performance", "IT subcontractor"],
  },
  "/experience/usa-government": {
    title: "U.S. Government IT Experience | Mitaja Corporation",
    description:
      "Mitaja's documented federal, state, and local government technology experience through subcontracting and teaming relationships.",
    keywords: ["federal government IT contractor", "state and local government IT services", "government IT services", "IT subcontractor"],
  },
  "/experience/international": {
    title: "International Government Experience | Mitaja",
    description:
      "Explore Mitaja's partner-supported consulting experience across international public-sector technology and digital-service programs.",
    keywords: ["government technology services", "systems integration", "IT professional services"],
  },
  "/experience/commercial": {
    title: "Commercial Technology Experience | Mitaja",
    description:
      "Technical staffing, application, infrastructure, database, and consulting support delivered for commercial enterprise clients.",
    keywords: ["IT staff augmentation", "IT professional services", "IT engineering services", "technical staffing"],
  },
  "/markets": {
    title: "Government & Enterprise Markets | Mitaja",
    description:
      "Mitaja supports federal, state and local agencies, government prime contractors, and commercial enterprise organizations.",
    keywords: ["government IT services", "federal government IT contractor", "state and local government IT services", "IT subcontractor"],
  },
  "/about": {
    title: "About Mitaja Corporation | Government IT Partner",
    description:
      "Learn about Mitaja Corporation, its mission-focused delivery model, contracting credentials, technical expertise, and company information.",
    keywords: ["IT professional services", "government IT services", "IT staff augmentation", "government technology services"],
  },
  "/about/careers": {
    title: "Technology Careers | Mitaja Corporation",
    description:
      "Explore career opportunities with Mitaja across engineering, infrastructure, databases, applications, cloud, and government IT delivery.",
    keywords: ["IT careers", "technology jobs", "IT professional services", "government IT services"],
  },
  "/resources": {
    title: "Contracting Resources | Mitaja Corporation",
    description:
      "Access Mitaja's capability statement, contracting credentials, bid categories, procurement information, and technology insights.",
    keywords: ["government IT services", "contracting information", "IT professional services", "capability statement"],
  },
  "/resources/capability-statement": {
    title: "Capability Statement | Mitaja Corporation",
    description:
      "Review and download Mitaja's capability statement, UEI, CAGE, NAICS codes, certifications, GSA information, and experience.",
    keywords: ["capability statement", "government contracting", "IT professional services", "IT engineering services"],
  },
  "/resources/contracting-information": {
    title: "Contracting Information | Mitaja Corporation",
    description:
      "UEI, CAGE, NAICS codes, GSA MAS, state certifications, and contracting capabilities for government procurement.",
    keywords: ["government contracting", "federal government IT contractor", "state and local government IT services", "NAICS codes"],
  },
  "/resources/insights": {
    title: "Insights & News | Mitaja Corporation",
    description:
      "Thought leadership on enterprise IT engineering, database and platform services, and government technology delivery.",
    keywords: ["IT professional services", "government IT services", "IT engineering services", "technology insights"],
  },
  "/resources/project-types": {
    title: "Types & Bid Categories | Mitaja Corporation",
    description:
      "Where Mitaja can bid across government and enterprise IT categories, with supporting capabilities and pursuit guidance.",
    keywords: ["government contracting", "IT professional services", "systems integration", "government IT services"],
  },
  "/technologies": {
    title: "Technologies | Mitaja Corporation",
    description:
      "Browse Mitaja's complete technology stack across infrastructure, network, database, application, Microsoft, cloud, web, mobile, and AI.",
    keywords: ["IT engineering services", "IT professional services", "software development services", "technology stack"],
  },
  "/contact": {
    title: "Contact Government Business Development | Mitaja",
    description:
      "Contact Mitaja regarding government contracting, teaming opportunities, proposals, technology delivery, and business development.",
    keywords: ["contact Mitaja", "government IT services", "IT subcontractor", "business development"],
  },
  "/privacy-policy": {
    title: "Privacy Policy | Mitaja Corporation",
    description:
      "How Mitaja Corporation collects, uses, and protects information submitted through this website.",
    keywords: ["privacy policy", "Mitaja Corporation"],
  },
  "/terms-website-notice": {
    title: "Terms & Website Notice | Mitaja Corporation",
    description:
      "Terms and conditions governing use of the Mitaja Corporation website.",
    keywords: ["terms of use", "website notice", "Mitaja Corporation"],
  },
  "/accessibility-statement": {
    title: "Accessibility Statement | Mitaja Corporation",
    description:
      "Mitaja's commitment to accessible digital experiences and how to report accessibility barriers.",
    keywords: ["accessibility statement", "WCAG", "Mitaja Corporation"],
  },
};

const SERVICE_NAMED_PAGES: Record<string, { name: string; description: string }> = {
  "/capabilities/infrastructure-network-engineering": {
    name: "Infrastructure & Network Engineering",
    description: "Enterprise network engineering, virtualization, Windows Server, VMware, modernization, migration, and infrastructure support.",
  },
  "/capabilities/database-data-platform": {
    name: "Database & Data Platform Services",
    description: "Oracle and SQL Server engineering, administration, schema management, data integrity, reporting, and platform support.",
  },
  "/capabilities/application-platform-engineering": {
    name: "Application & Platform Engineering",
    description: "Enterprise application support, WebSphere, integration, platform engineering, maintenance, testing, and technical delivery.",
  },
  "/capabilities/microsoft-solutions": {
    name: "Microsoft Power Platform & SharePoint",
    description: "Power Apps, Power Automate, Power BI, SharePoint, workflow integration, implementation, training, and user enablement.",
  },
  "/capabilities/web-digital-solutions": {
    name: "Government Web & Digital Solutions",
    description: "Accessible websites, CMS modernization, web portals, content migration, integration, and public-sector digital solutions.",
  },
  "/capabilities/mobile-application-development": {
    name: "Mobile Application Development",
    description: "Secure cross-platform mobile applications, API integration, testing, and accessibility-aware mobile user experiences.",
  },
  "/capabilities/ai-intelligent-engineering": {
    name: "AI & Intelligent Engineering Services",
    description: "AI-assisted engineering, workflow automation, data pipelines, business intelligence, and responsible machine-learning integration.",
  },
  "/capabilities/cloud-services": {
    name: "Cloud Services",
    description: "Microsoft Azure cloud engineering, IaaS, PaaS, hybrid cloud, migration, DevOps, and governance for enterprise and government programs.",
  },
};

function resolveMeta(pathname: string): PageMeta {
  const exact = pageMeta[pathname];
  if (exact) return exact;

  // Insight article detail pages use the article title for a unique page title.
  const insightMatch = pathname.match(/^\/resources\/insights\/([^/]+)$/);
  if (insightMatch) {
    const slug = insightMatch[1];
    const insight = insights.find((item) => item.slug === slug);
    if (insight) {
      return {
        title: `${insight.title} | Mitaja Corporation`,
        description: insight.summary,
        ogType: "article",
        keywords: ["IT professional services", "government IT services", insight.categories[0] || "IT engineering"],
      };
    }
    return {
      title: "Insight | Mitaja Corporation",
      description:
        "Practical thought leadership on enterprise IT engineering, database and platform services, and government technology delivery.",
      keywords: ["IT professional services", "government IT services"],
    };
  }

  return { title: defaultTitle, description: defaultDescription, keywords: defaultKeywords };
}

// Find top-level page last segment + breadcrumb trail for a pathname.
function buildBreadcrumb(pathname: string): { name: string; url: string }[] {
  const parts = pathname.split("/").filter(Boolean);
  // Map known segment labels to human-readable names for breadcrumbs.
  const segmentLabels: Record<string, string> = {
    capabilities: "Capabilities",
    experience: "Experience",
    markets: "Markets",
    about: "About",
    careers: "Careers",
    resources: "Resources",
    insights: "Insights",
    technologies: "Technologies",
    contact: "Contact",
    "privacy-policy": "Privacy Policy",
    "terms-website-notice": "Terms & Website Notice",
    "accessibility-statement": "Accessibility Statement",
    "usa-government": "U.S. Government Experience",
    international: "International Experience",
    commercial: "Commercial Experience",
    "infrastructure-network-engineering": "Infrastructure & Network Engineering",
    "database-data-platform": "Database & Data Platform Services",
    "application-platform-engineering": "Application & Platform Engineering",
    "microsoft-solutions": "Microsoft Solutions",
    "web-digital-solutions": "Web & Digital Solutions",
    "mobile-application-development": "Mobile Application Development",
    "ai-intelligent-engineering": "AI & Intelligent Engineering",
    "cloud-services": "Cloud Services",
    "capability-statement": "Capability Statement",
    "contracting-information": "Contracting Information",
    "project-types": "Types & Bid Categories",
  };

  const crumbs: { name: string; url: string }[] = [
    { name: "Home", url: `${SITE_URL}/` },
  ];

  let accumulated = "";
  parts.forEach((part, index) => {
    accumulated += `/${part}`;
    const name =
      segmentLabels[part] ||
      (index === parts.length - 1 ? part.replace(/-/g, " ") : part);
    crumbs.push({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      url: `${SITE_URL}${accumulated}`,
    });
  });

  return crumbs;
}

function setMetaTag(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setMetaAttribute(selector: string, attribute: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    const attrParts = selector.match(/\[([a-z]+)="(.*?)"\]/);
    if (attrParts) {
      el.setAttribute(attrParts[1], attrParts[2]);
    }
    document.head.appendChild(el);
  }
  el.setAttribute(attribute, content);
}

function setLinkTag(rel: string, href: string) {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.href = href;
}

function renderJsonLd(scriptId: string, data: object) {
  // Remove an existing script with this id to avoid duplicates on navigation.
  const existing = document.getElementById(scriptId);
  if (existing) existing.remove();
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = scriptId;
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

function normalizePathname(pathname: string): string {
  return pathname.endsWith("/") && pathname !== "/" ? pathname.slice(0, -1) : pathname;
}

export default function SEO() {
  const { pathname } = useLocation();

  useEffect(() => {
    const path = normalizePathname(pathname);
    const meta = resolveMeta(path);
    const canonicalUrl = `${SITE_URL}${path === "/" ? "/" : path}`;

    document.title = meta.title;

    // Meta description
    setMetaTag("description", meta.description);
    // Keywords
    setMetaTag("keywords", meta.keywords.join(", "));

    // Canonical
    setLinkTag("canonical", canonicalUrl);

    // Robots — indexable by default
    setMetaTag("robots", "index, follow, max-image-preview:large");

    // Open Graph
    setMetaAttribute('[property="og:title"]', "content", meta.title);
    setMetaAttribute('[property="og:description"]', "content", meta.description);
    setMetaAttribute('[property="og:url"]', "content", canonicalUrl);
    setMetaAttribute('[property="og:type"]', "content", meta.ogType === "article" ? "article" : "website");
    setMetaAttribute('[property="og:site_name"]', "content", SITE_NAME);
    setMetaAttribute('[property="og:locale"]', "content", "en_US");

    // Twitter
    setMetaAttribute('[name="twitter:card"]', "content", "summary_large_image");
    setMetaAttribute('[name="twitter:title"]', "content", meta.title);
    setMetaAttribute('[name="twitter:description"]', "content", meta.description);

    // Breadcrumb JSON-LD
    const crumbList = buildBreadcrumb(path);
    renderJsonLd("breadcrumb-jsonld", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: crumbList.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: c.name,
        item: c.url,
      })),
    });

    // WebPage JSON-LD
    renderJsonLd("webpage-jsonld", {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${canonicalUrl}#webpage`,
      url: canonicalUrl,
      name: meta.title,
      description: meta.description,
      isPartOf: { "@id": `${SITE_URL}/#website` },
      about: { "@id": `${SITE_URL}/#organization` },
      breadcrumb: { "@id": `${canonicalUrl}#breadcrumb` },
      inLanguage: "en-US",
    });

    // Service JSON-LD for capability pages
    const service = SERVICE_NAMED_PAGES[path];
    if (service) {
      renderJsonLd("service-jsonld", {
        "@context": "https://schema.org",
        "@type": "Service",
        name: service.name,
        description: service.description,
        url: canonicalUrl,
        serviceType: service.name,
        provider: { "@id": `${SITE_URL}/#organization` },
        areaServed: ["United States", "Federal government", "State and local government"],
        audience: {
          "@type": "Audience",
          audienceType: "Government agencies, prime contractors, and commercial enterprises",
        },
      });
    } else {
      // Remove stale service JSON-LD when leaving a capability page.
      const stale = document.getElementById("service-jsonld");
      if (stale) stale.remove();
    }

    // Article JSON-LD for insight detail pages.
    const insightMatch = path.match(/^\/resources\/insights\/([^/]+)$/);
    if (insightMatch && meta.ogType === "article") {
      const insight = insights.find((item) => item.slug === insightMatch[1]);
      renderJsonLd("article-jsonld", {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: meta.title,
        description: meta.description,
        url: canonicalUrl,
        about: { "@id": `${SITE_URL}/#organization` },
        publisher: { "@id": `${SITE_URL}/#organization` },
        author: { "@id": `${SITE_URL}/#organization` },
        mainEntityOfPage: { "@id": `${canonicalUrl}#webpage` },
        inLanguage: "en-US",
        ...(insight?.date ? { datePublished: insight.date } : {}),
      });
    } else {
      const staleArticle = document.getElementById("article-jsonld");
      if (staleArticle) staleArticle.remove();
    }
  }, [pathname]);

  // Keep constants referenced to avoid unused import lint issues.
  void COMPANY;
  return null;
}